import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../../api/client';
import Code from '../Code';
import type { Disposition, EvoValue, EvolutionHistoryEvent, EvolutionLearningDatasetRow } from '../../types';
import { formatEvoValue, shortDate } from './format';

const DISPOSITIONS: Disposition[] = ['monitor', 'simulate', 'create_option', 'accelerate', 'escalate', 'structural_rewire'];

export default function HistoryCompoundingPanel() {
  const events = useQuery({ queryKey: ['evolution', 'history', 'events'], queryFn: api.evolutionHistoryEvents });
  const dataset = useQuery({ queryKey: ['evolution', 'history', 'learning-dataset'], queryFn: api.evolutionLearningDataset });

  if (events.isLoading || dataset.isLoading) return <div className="panel"><div className="small faint">Loading evolution history...</div></div>;
  if (events.isError || dataset.isError) return <div className="panel"><div className="small faint">Evolution history unavailable.</div></div>;

  const rows = events.data ?? [];
  if (rows.length === 0) return <ReadyToLearn />;

  return <HistoryBlocks events={rows} dataset={dataset.data ?? []} />;
}

function ReadyToLearn() {
  return (
    <section id="evolution-history" className="panel" style={{ marginTop: 14, borderLeft: '3px solid var(--azura)' }}>
      <div className="ph">Evolution history</div>
      <div className="h2">Ready to learn.</div>
      <div className="grid2" style={{ marginTop: 12 }}>
        {['decider', 'rule_id', 'baseline', 'expected delta', 'outcome window'].map((field) => (
          <div className="field" key={field}>
            <div className="field-label">{field}</div>
            <div className="field-value">captured with each disposition</div>
          </div>
        ))}
      </div>
      <div className="panel" style={{ marginTop: 12, background: 'var(--panel2)', opacity: 0.72 }}>
        <div className="small faint" style={{ marginBottom: 6 }}>Illustrative example row</div>
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span><Code id="S-04" kind="signal" compact /> · accelerate</span>
          <span className="mono small">manufacturing.oee_projected_floor_breach</span>
          <span className="small">outcome window pending</span>
        </div>
      </div>
      <div className="small" style={{ marginTop: 12 }}>
        No evolution decisions logged yet. Each disposition you take becomes calibration history.
      </div>
    </section>
  );
}

function HistoryBlocks({ events, dataset }: { events: EvolutionHistoryEvent[]; dataset: EvolutionLearningDatasetRow[] }) {
  const distribution = useMemo(() => dispositionDistribution(events), [events]);
  const trend = useMemo(() => calibrationTrend(dataset), [dataset]);
  const modelVersions = unique(events.map((event) => event.model_version));
  const readySignals = enoughHistorySignals(events);
  const overrides = events.filter((event) => event.override_reason);

  return (
    <section id="evolution-history" className="panel" style={{ marginTop: 14, borderLeft: '3px solid var(--azura)' }}>
      <div className="ph">Evolution history</div>
      <div className="h2">Compounding decision memory</div>

      <div className="qhead" style={{ marginTop: 12 }}><span className="qdot" style={{ background: 'var(--azura)' }} />Decisions over time</div>
      <table>
        <thead>
          <tr>
            <th>When</th>
            <th>Signal</th>
            <th>Disposition</th>
            <th>Rule</th>
            <th>Outcome window</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.event_id}>
              <td className="mono">{shortDate(event.timestamp)}</td>
              <td><Code id={event.signal_id} kind="signal" compact /></td>
              <td><span className="tag muted">{formatDisposition(event.disposition)}</span></td>
              <td className="mono small">{event.rule_id}</td>
              <td>{event.actual_outcome_window}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid2" style={{ marginTop: 14 }}>
        <div>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--olive)' }} />Predictions vs actuals</div>
          <table>
            <thead>
              <tr>
                <th>Signal</th>
                <th>Expected</th>
                <th>Actual</th>
              </tr>
            </thead>
            <tbody>
              {dataset.map((row) => (
                <tr key={row.decision_log_id}>
                  <td><Code id={row.signal_id} kind="signal" compact /></td>
                  <td>{formatMaybeValue(firstValue(row.expected_metric_delta))}</td>
                  <td>{formatMaybeValue(firstActualValue(row.actual_outcome))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--red)' }} />Calibration-error trend</div>
          {trend.length === 0 ? (
            <div className="small">Awaiting actual outcomes.</div>
          ) : (
            <div style={{ height: 220, minWidth: 0 }}>
              <ResponsiveContainer>
                <LineChart data={trend} margin={{ top: 10, right: 20, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" />
                  <XAxis dataKey="period" stroke="var(--faint)" tick={{ fill: 'var(--faint)', fontSize: 12 }} />
                  <YAxis stroke="var(--faint)" tick={{ fill: 'var(--faint)', fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: 'var(--panel)', border: '1px solid var(--line)', color: 'var(--ink)' }} />
                  <Line type="monotone" dataKey="error" name="Calibration error" stroke="var(--red)" strokeWidth={3} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="grid2" style={{ marginTop: 14 }}>
        <div>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Disposition distribution</div>
          <div className="feed">
            {DISPOSITIONS.map((disposition) => (
              <div className="field" key={disposition}>
                <div className="field-label">{formatDisposition(disposition)}</div>
                <div className="field-value">
                  <div style={{ height: 8, background: 'var(--line)', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ width: distribution[disposition], height: '100%', background: 'var(--amber)' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--gold)' }} />Override reasons</div>
          {overrides.length === 0 ? (
            <div className="small">No overrides logged.</div>
          ) : (
            <div className="feed">
              {overrides.map((event) => (
                <div className="field" key={event.event_id}>
                  <div className="field-label">{event.disposition_by ?? 'operator'}</div>
                  <div className="field-value">{event.override_reason}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid2" style={{ marginTop: 14 }}>
        <div>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--blue)' }} />Model/version changes</div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {modelVersions.map((version) => <span key={version} className="tag blue">{version}</span>)}
          </div>
        </div>
        <div>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Signals with enough history</div>
          {readySignals.length === 0 ? (
            <div className="small">No signal has enough evolution history yet.</div>
          ) : (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {readySignals.map((signal) => <Code key={signal} id={signal} kind="signal" compact />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function dispositionDistribution(events: EvolutionHistoryEvent[]) {
  const counts = Object.fromEntries(DISPOSITIONS.map((disposition) => [disposition, 0])) as Record<Disposition, number>;
  events.forEach((event) => {
    counts[event.disposition] += 1;
  });
  const max = Math.max(...Object.values(counts), 1);
  return Object.fromEntries(
    DISPOSITIONS.map((disposition) => [disposition, `${Math.round((counts[disposition] / max) * 100)}%`]),
  ) as Record<Disposition, string>;
}

function calibrationTrend(rows: EvolutionLearningDatasetRow[]) {
  return rows.flatMap((row) => {
    const expected = firstValue(row.expected_metric_delta);
    const actual = firstActualValue(row.actual_outcome);
    if (!expected || !actual) return [];
    return [{
      period: shortDate(row.timestamp),
      error: Math.abs(actual.value - expected.value),
    }];
  });
}

function enoughHistorySignals(events: EvolutionHistoryEvent[]) {
  const counts = events.reduce<Record<string, number>>((acc, event) => {
    acc[event.signal_id] = (acc[event.signal_id] ?? 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).flatMap(([signal, count]) => (count >= 3 ? [signal] : []));
}

function firstValue(values: Record<string, EvoValue>) {
  return Object.values(values)[0] ?? null;
}

function firstActualValue(actual?: Record<string, unknown> | null) {
  if (!actual) return null;
  return Object.values(actual).find(isEvoValue) ?? null;
}

function isEvoValue(value: unknown): value is EvoValue {
  return typeof value === 'object' && value !== null && 'value' in value && 'unit' in value && 'provenance' in value;
}

function formatMaybeValue(value: EvoValue | null) {
  return value ? formatEvoValue(value, { signed: true }) : 'pending';
}

function formatDisposition(value: string) {
  return value.replaceAll('_', ' ');
}

function unique(values: string[]) {
  return Array.from(new Set(values));
}
